// src/components/notifications/NotificationsBell.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Bell, CheckCircle2, AlertTriangle, Loader2, X } from "lucide-react";
import { useNotifications } from "./NotificationsProvider";

const OVERLAY_EVENT = "pixelads:overlay:open";

/* ----------------------------- helpers ----------------------------- */
function timeAgo(ts) {
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  if (!Number.isFinite(diff) || diff < 0) return "";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

/**
 * Small preview for notification.
 * - Images: <img>
 * - Videos: lazy-mount <video> when the row is visible; play only on hover
 */
function GeneratorPreview({ media }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!containerRef.current || isVisible) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.15 }
    );

    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [isVisible]);

  const commonClass =
    "w-11 h-11 rounded-xl object-cover border border-white/10 bg-white/5 flex-shrink-0 overflow-hidden";

  if (!media?.src) {
    return <div className={commonClass} ref={containerRef} />;
  }

  if (media.type === "image") {
    return (
      <img
        ref={containerRef}
        src={media.src}
        alt="Preview"
        className={commonClass}
        draggable={false}
        loading="lazy"
      />
    );
  }

  return (
    <div ref={containerRef} className={commonClass}>
      {isVisible ? (
        <video
          ref={videoRef}
          src={media.src}
          className="w-full h-full rounded-xl object-cover"
          muted
          loop
          playsInline
          preload="metadata"
          onMouseEnter={() => {
            const v = videoRef.current;
            if (!v) return;
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          }}
          onMouseLeave={() => {
            const v = videoRef.current;
            if (!v) return;
            v.pause();
          }}
        />
      ) : null}
    </div>
  );
}

export default function NotificationsBell() {
  const {
    unreadCount,
    panelOpen,
    setPanelOpen,
    timeline,
    nextCursor,
    timelineLoading,
    fetchMore,
    dismissNotification,
    openNotification,
    buildNotificationCopy,
  } = useNotifications();

  const panelRef = useRef(null);
  const sentinelRef = useRef(null);

  const dispatchOverlayOpen = useCallback((source) => {
    try {
      window.dispatchEvent(
        new CustomEvent(OVERLAY_EVENT, { detail: { source } })
      );
    } catch {
      // ignore
    }
  }, []);

  // ✅ Close notifications when another overlay opens (avatar / product dropdown)
  useEffect(() => {
    const onOverlayOpen = (e) => {
      const src = e?.detail?.source;
      if (src && src !== "notifications") setPanelOpen(false);
    };
    window.addEventListener(OVERLAY_EVENT, onOverlayOpen);
    return () => window.removeEventListener(OVERLAY_EVENT, onOverlayOpen);
  }, [setPanelOpen]);

  // close on outside click
  useEffect(() => {
    if (!panelOpen) return;

    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [panelOpen, setPanelOpen]);

  // infinite load with IntersectionObserver
  useEffect(() => {
    if (!panelOpen) return;
    if (!sentinelRef.current) return;

    const el = sentinelRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((en) => en.isIntersecting);
        if (hit) fetchMore();
      },
      { root: panelRef.current, threshold: 0.1 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchMore, panelOpen]);

  const onOpen = useCallback(() => {
    setPanelOpen((v) => {
      const next = !v;
      if (next) dispatchOverlayOpen("notifications");
      return next;
    });
  }, [dispatchOverlayOpen, setPanelOpen]);

  const rows = useMemo(() => {
    const arr = Array.isArray(timeline) ? [...timeline] : [];
    arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return arr;
  }, [timeline]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onOpen}
        className="relative p-2 rounded-full hover:bg-[#2A2A40] select-none"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell size={18} className="text-white" strokeWidth={2} />
        {unreadCount > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FC2783] text-white text-[11px] font-bold flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </button>

      {/* Panel */}
      <div
        ref={panelRef}
        className={`absolute right-0 top-full mt-3 w-[520px] max-w-[calc(100vw-2rem)] bg-[#181828] border border-[#3a3a4d] rounded-2xl shadow-2xl shadow-black/40 z-50 overflow-hidden transform origin-top-right transition-all duration-150 ${
          panelOpen
            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
            : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="px-4 py-3 flex items-center justify-between border-b border-white/5 bg-gradient-to-r from-[#1f1f3a] to-[#181828]">
          <div className="text-white font-semibold">Notifications</div>
          <button
            type="button"
            onClick={() => setPanelOpen(false)}
            className="p-1 rounded-lg hover:bg-white/5 text-white/70 hover:text-white"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[460px] overflow-auto">
          {timelineLoading && rows.length === 0 ? (
            <div className="p-6 flex items-center gap-2 text-white/70">
              <Loader2 className="animate-spin" size={16} />
              Loading…
            </div>
          ) : null}

          {rows.length === 0 && !timelineLoading ? (
            <div className="p-6 text-white/60 text-sm">No notifications in the last 7 days.</div>
          ) : null}

          <div className="divide-y divide-white/5">
            {rows.map((n) => {
              const isSuccess = n.type === "asset_success";
              const isFail = n.type === "asset_failed";

              const icon = isSuccess ? (
                <CheckCircle2 size={16} className="text-[#4ade80]" />
              ) : isFail ? (
                <AlertTriangle size={16} className="text-[#f87171]" />
              ) : (
                <Bell size={16} className="text-white/70" />
              );

              const copy = buildNotificationCopy(n);

              const isOpened = Boolean(n.clicked_at) || Number(n.click_count || 0) > 0;
              const showDot = !isOpened;

              return (
                <div
                  key={n.id}
                  className={`px-4 py-3 hover:bg-white/5 transition-colors ${
                    isOpened ? "opacity-80" : "opacity-100"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <GeneratorPreview media={copy?.previewMedia} />

                    <button
                      type="button"
                      onClick={() => openNotification(n)}
                      className="text-left min-w-0 flex-1"
                      aria-label="Open notification"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="mt-[2px]">{icon}</span>

                          <div
                            className={`text-sm truncate ${
                              showDot ? "font-semibold text-white" : "font-medium text-white"
                            }`}
                          >
                            {copy.title}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="text-[11px] text-white/50">{timeAgo(n.created_at)}</div>

                          {showDot ? (
                            <span
                              className="w-2.5 h-2.5 rounded-full bg-[#3b82f6]"
                              aria-label="Unopened"
                              title="Unopened"
                            />
                          ) : null}
                        </div>
                      </div>

                      <div
                        className={`text-[12px] leading-5 mt-0.5 ${
                          showDot ? "text-white/85 font-medium" : "text-white/70"
                        }`}
                      >
                        {copy.message}
                      </div>

                      {copy.createdText ? (
                        <div className="text-[11px] text-white/45 mt-1.5">{copy.createdText}</div>
                      ) : null}
                    </button>

                    <button
                      type="button"
                      onClick={() => dismissNotification(n.id)}
                      className="p-1 rounded-lg hover:bg-white/5 text-white/60 hover:text-white"
                      aria-label="Dismiss"
                      title="Dismiss"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {nextCursor ? (
            <div ref={sentinelRef} className="p-4 flex items-center justify-center">
              {timelineLoading ? (
                <div className="flex items-center gap-2 text-white/60 text-sm">
                  <Loader2 className="animate-spin" size={16} />
                  Loading more…
                </div>
              ) : (
                <div className="text-white/40 text-xs">Scroll to load more…</div>
              )}
            </div>
          ) : (
            <div className="p-4 text-center text-white/40 text-xs">
              You’ve reached the end (last 7 days).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
